import type { Locale, StateV2 } from '@superposition/domain';
import { messages, type Messages } from '../i18n/index.js';

/**
 * клиент API. токен сессии живёт только в памяти: ни localStorage, ни cookie,
 * ни URL. после перезагрузки нужна новая проверка initData.
 */

export interface ApiErrorBody {
  error: { code: string; message: string; path?: string; detail?: string };
  requestId?: string;
  revision?: number;
  state?: StateV2;
  access?: { telegramId: string; expiredAt: string | null; renewUrl: string; buyUrl?: string | null };
}

export type ApiFailureKind =
  | 'offline'
  | 'timeout'
  | 'unauthorized'
  | 'no_access'
  | 'conflict'
  | 'invalid'
  | 'too_large'
  | 'server'
  | 'unknown';

export class ApiError extends Error {
  readonly kind: ApiFailureKind;
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;
  readonly body?: ApiErrorBody;

  constructor(
    kind: ApiFailureKind,
    status: number,
    code: string,
    message: string,
    body?: ApiErrorBody,
  ) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.code = code;
    this.body = body;
    if (body?.requestId) this.requestId = body.requestId;
  }
}

function kindFor(status: number, code: string): ApiFailureKind {
  if (status === 401) return 'unauthorized';
  if (status === 403 && code === 'no_access') return 'no_access';
  if (status === 409) return 'conflict';
  if (status === 413) return 'too_large';
  if (status === 400 || status === 415 || status === 422) return 'invalid';
  if (status >= 500) return 'server';
  return code === 'offline' ? 'offline' : 'unknown';
}

let sessionToken: string | null = null;

export function setToken(token: string | null): void {
  sessionToken = token;
}

export function hasToken(): boolean {
  return sessionToken !== null;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  timeoutMs?: number;
  auth?: boolean;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, timeoutMs = 15_000, auth = true } = opts;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  // язык: этап 1. запрос не знает языка профиля
  const t = messages();
  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (auth) {
    if (!sessionToken) {
      clearTimeout(timer);
      throw new ApiError('unauthorized', 401, 'no_session', t.errors.noSession);
    }
    headers['authorization'] = `Bearer ${sessionToken}`;
  }

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
      credentials: 'same-origin',
      cache: 'no-store',
    });
  } catch (err) {
    clearTimeout(timer);
    const aborted = err instanceof Error && err.name === 'AbortError';
    throw new ApiError(
      aborted ? 'timeout' : 'offline',
      0,
      aborted ? 'timeout' : 'offline',
      aborted ? t.errors.timeout : t.errors.offline,
    );
  } finally {
    clearTimeout(timer);
  }

  if (res.status === 204) return undefined as T;

  let parsed: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (!res.ok) {
    const b = parsed as ApiErrorBody | null;
    const code = b?.error?.code ?? `http_${res.status}`;
    const message = b?.error?.message ?? t.errors.requestFailed;
    throw new ApiError(kindFor(res.status, code), res.status, code, message, b ?? undefined);
  }

  return parsed as T;
}

// ——— маршруты ———

export interface AuthResult {
  token: string;
  expiresAt: string;
  user: { id: string; timezone: string };
  schemaVersion: number;
}

export async function authTelegram(initData: string, timezone: string): Promise<AuthResult> {
  const result = await request<AuthResult>('/api/v1/auth/telegram', {
    method: 'POST',
    body: { initData, timezone },
    auth: false,
  });
  setToken(result.token);
  return result;
}

export interface StateEnvelope {
  revision: number;
  state: StateV2;
}

export function fetchState(): Promise<StateEnvelope> {
  return request<StateEnvelope>('/api/v1/state');
}

export function saveState(expectedRevision: number, state: StateV2): Promise<StateEnvelope> {
  return request<StateEnvelope>('/api/v1/state', {
    method: 'PUT',
    body: { expectedRevision, state },
  });
}

export interface BookSpan {
  text: string;
  em?: boolean;
  strong?: boolean;
}

export type BookBlock =
  | { kind: 'p' | 'h' | 'li' | 'quote'; spans: BookSpan[] }
  | { kind: 'table'; rows: string[][] }
  | { kind: 'img'; role: 'cover' | 'figure'; src: string; w: number; h: number; alt: string; caption?: string }
  | { kind: 'audio'; src: string; title: string; sphere: string; duration: number };

export interface BookIndex {
  title: string;
  chapters: { id: string; title: string; blocks: number }[];
}

export interface BookChapter {
  id: string;
  title: string;
  blocks: BookBlock[];
}

export interface LakePractice {
  sphere: string;
  src: string;
  title: string;
  duration: number;
}

export function fetchLake(): Promise<{ practices: LakePractice[] }> {
  return request<{ practices: LakePractice[] }>('/api/v1/lake');
}

export interface ObservationWords {
  lead: string;
  body: string;
  act: string;
}

/** живые формулировки наблюдателя; сервер вернет тот же текст, если модель не справилась */
export function rephraseObservation(w: ObservationWords): Promise<ObservationWords & { rephrased: boolean }> {
  return request<ObservationWords & { rephrased: boolean }>('/api/v1/observe/rephrase', {
    method: 'POST',
    body: w,
    timeoutMs: 9_000,
  });
}

export function fetchBook(): Promise<BookIndex> {
  return request<BookIndex>('/api/v1/book');
}

export function fetchChapter(id: string): Promise<BookChapter> {
  return request<BookChapter>(`/api/v1/book/${encodeURIComponent(id)}`, { timeoutMs: 30_000 });
}

export interface ProfileSettings {
  name: string | null;
  remindAt: string | null;
  remindersOn: boolean;
  gender: 'm' | 'f' | 'n' | null;
  /** язык из профиля; старый сервер поля не присылает — значит русский */
  locale?: Locale;
  /** какие языки можно выбрать */
  locales?: Locale[];
}

export function fetchProfile(): Promise<ProfileSettings> {
  return request<ProfileSettings>('/api/v1/profile');
}

export function saveProfile(p: ProfileSettings): Promise<ProfileSettings> {
  return request<ProfileSettings>('/api/v1/profile', { method: 'PUT', body: p });
}

export function saveLocale(locale: Locale): Promise<ProfileSettings> {
  return request<ProfileSettings>('/api/v1/profile/locale', { method: 'PUT', body: { locale } });
}

export function deleteAccount(): Promise<void> {
  return request<void>('/api/v1/account/delete', {
    method: 'POST',
    body: { confirmation: 'delete my data' },
  });
}

/** человеческое объяснение отказа — без кодов и стектрейсов */
export function describeFailure(err: unknown, t: Messages): string {
  if (!(err instanceof ApiError)) return t.errors.unknown;
  switch (err.kind) {
    case 'offline':
      return t.errors.offlineUnsaved;
    case 'timeout':
      return t.errors.timeoutUnsaved;
    case 'unauthorized':
      return t.errors.unauthorized;
    case 'no_access':
      return t.errors.noAccess;
    case 'conflict':
      return t.errors.conflict;
    case 'too_large':
      return t.errors.tooLarge;
    case 'invalid':
      return err.message;
    case 'server':
      return t.errors.server({ requestId: err.requestId ? err.requestId.slice(0, 8) : null });
    default:
      return err.message;
  }
}
